import { css, cx } from "@emotion/css";
import { borderRadius, fontSize, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import { remToPx } from "../utils/remToPx";
export interface SelectOptionLayoutProps {
    label: string;
    description?: string;
    prefix?: React.ReactElement;
    className?: string;
}
export function SelectOptionLayout({ label, description, prefix, className }: SelectOptionLayoutProps) {
    return (<div className={cx(styles.container, { [styles.containerWithDescription]: !!description }, className)}>
            {prefix && description ? (<div className={cx(styles.prefix, styles.prefixTopContainer)}>
                    <span className={styles.prefixTop}>{prefix}</span>
                </div>) : (<div className={cx(styles.prefix, styles.prefixCenterContainer)}>{prefix}</div>)}
            <div className={styles.textContainer}>
                <span className={styles.label}>{label}</span>
                {description && <span className={styles.description}>{description}</span>}
            </div>
        </div>);
}
// Known heights are required for virtualization of the listbox
const containerPaddingVertical = space[6];
const labelLineHeight = fontSize.xLarge;
const descriptionLineHeight = fontSize.large;
export const selectOptionHeight = 2 * remToPx(containerPaddingVertical) + remToPx(labelLineHeight);
export const selectOptionWithDescriptionHeight = selectOptionHeight + remToPx(descriptionLineHeight);
const styles = {
    container: css({
        display: "flex",
        boxSizing: "border-box",
        height: selectOptionHeight,
        padding: `${containerPaddingVertical} ${space[12]}`,
        gap: space[6],
        borderRadius: borderRadius.small,
    }),
    containerWithDescription: css({
        height: selectOptionWithDescriptionHeight,
    }),
    prefix: css({
        color: themeTokens.color.icon.secondary,
    }),
    prefixCenterContainer: css({
        alignSelf: "center",
    }),
    prefixTopContainer: css({
        // This text token needs to be the same as the label's, so that the prefix can use vertical-align: text-top styling
        font: text.body.regular.large,
    }),
    prefixTop: css({
        display: "inline-block",
        verticalAlign: "text-top",
    }),
    textContainer: css({
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    }),
    label: css({
        font: text.body.regular.large,
        lineHeight: labelLineHeight,
        color: themeTokens.color.text.primary,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    }),
    description: css({
        font: text.body.regular.medium,
        lineHeight: descriptionLineHeight,
        color: themeTokens.color.text.secondary,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    }),
};
